import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { scheduleLifecyclePeriodLockedErrorSchema } from "@common/schemas/domains/accounting/scheduleLifecycle.schema";
import {
    scheduleLinkedSourceConflictErrorSchema,
    scheduleSourceMoveConflictErrorSchema,
} from "@common/schemas/domains/accounting/scheduleLinkedSource.schema";
import {
    approveAccrualReviewBodySchema,
    reverseApproveAccrualReviewBodySchema,
    undoAccrualReviewBodySchema,
    accrualReviewApproveResponseSchema,
    accrualReviewUndoResponseSchema,
    bulkApproveAccrualReviewBodySchema,
    bulkApproveAccrualReviewResponseSchema,
    hasMatchableDocumentsResponseSchema,
    bulkUnmatchInvoicesBodySchema,
    bulkUnmatchInvoicesResponseSchema,
    accrualReviewWorkspacePathParamsSchema,
} from "../schemas/domains/accrualReview/accrual-review.schema";

const c = initContract();

export const accrualReviewContract = c.router(
    {
        hasMatchableDocuments: {
            path: "/:workspaceId/has-matchable-documents",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "GET",
            responses: {
                200: hasMatchableDocumentsResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        approve: {
            path: "/:workspaceId/approve",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: approveAccrualReviewBodySchema,
            responses: {
                200: accrualReviewApproveResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: z.union([
                    scheduleLifecyclePeriodLockedErrorSchema,
                    scheduleLinkedSourceConflictErrorSchema,
                    scheduleSourceMoveConflictErrorSchema,
                    ErrorMessageSchema,
                ]),
            },
        },
        bulkApprove: {
            path: "/:workspaceId/bulk-approve",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: bulkApproveAccrualReviewBodySchema,
            responses: {
                200: bulkApproveAccrualReviewResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        reverseApprove: {
            path: "/:workspaceId/reverse-approve",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: reverseApproveAccrualReviewBodySchema,
            responses: {
                200: accrualReviewApproveResponseSchema,
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
                409: z.union([
                    scheduleLifecyclePeriodLockedErrorSchema,
                    scheduleLinkedSourceConflictErrorSchema,
                    scheduleSourceMoveConflictErrorSchema,
                    ErrorMessageSchema,
                ]),
            },
        },
        undo: {
            path: "/:workspaceId/undo",
            pathParams: z.object({
                workspaceId: z.string().uuid(),
            }),
            method: "POST",
            body: undoAccrualReviewBodySchema,
            responses: {
                200: accrualReviewUndoResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        bulkUnmatchInvoices: {
            path: "/:workspaceId/bulk-unmatch-invoices",
            pathParams: accrualReviewWorkspacePathParamsSchema,
            method: "POST",
            body: bulkUnmatchInvoicesBodySchema,
            responses: {
                200: bulkUnmatchInvoicesResponseSchema,
                400: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/accrual-review",
    },
);
